import { formatDateWithDynamicTimezone } from "@marketplace/shared/utils";
import {
  Center,
  ConditionalRendering,
  Loader,
  Modal,
  Progress,
  Text
} from "@marketplace/ui";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useTranslation } from "react-i18next";
import { MdErrorOutline } from "react-icons/md";

import { productUsageModalAtom } from "../../atoms/product-usage-modal-atom";
import { useGetProductUsageQuery } from "../../hooks/queries/use-get-product-usage-query";

const formatThousand = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return "0";

  const num = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(num)) return "0";

  return num.toLocaleString("id-ID");
};

const capitalizeFirstLetter = (value: string): string => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const ProductUsageModal = () => {
  const { visible, orderItemId } = useAtomValue(productUsageModalAtom);
  const resetModal = useResetAtom(productUsageModalAtom);
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage.productUsageModal"
  });

  const {
    data,
    isLoading: isLoadingUsage,
    isError
  } = useGetProductUsageQuery({
    queryParams: { orderItemId: orderItemId! },
    options: {
      enabled: visible && !!orderItemId,
      throwOnError: false,
      retry: false
    }
  });

  const usageData = data?.data;

  const isValidEndDate =
    !!usageData?.end_date && usageData.end_date !== "0001-01-01T00:00:00Z";

  return (
    <Modal
      opened={visible}
      onClose={resetModal}
      title={
        <Text
          type="heading"
          size="l"
          weight="semibold"
          className="text-fjord-grey-500 text-h-s md:text-h-l "
        >
          {t("title")}
        </Text>
      }
      classNames={{
        header:
          "!p-6 !min-h-fit border-solid border-0 border-b border-mischka-100",
        body: "p-6 pb-6"
      }}
      size={526}
    >
      <ConditionalRendering condition={isLoadingUsage}>
        <Center className="min-h-[284px]">
          <Loader />
        </Center>
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoadingUsage && isError}>
        <Center className="min-h-[284px]">
          <div className="flex flex-col items-center justify-center gap-2 text-fjord-grey-400">
            <MdErrorOutline size={48} />
            <Text>{t("errorUsage")}</Text>
          </div>
        </Center>
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoadingUsage && !isError}>
        <div className="space-y-2 pb-0 flex flex-col">
          <div className="block space-y-2 py-6">
            <ConditionalRendering
              condition={usageData?.product_service === "period"}
            >
              <Text className="mb-1 text-fjord-grey-500">
                {t("usageTypePeriod")}
              </Text>
            </ConditionalRendering>
            <ConditionalRendering
              condition={usageData?.product_service === "quota"}
            >
              <Text className="mb-1 text-fjord-grey-500">
                {t("usageTypeQuota")}
              </Text>
            </ConditionalRendering>
            <ConditionalRendering
              condition={usageData?.product_service === "quota"}
            >
              <Text size="l" weight="semibold">
                {formatThousand(usageData?.used_value ?? 0)} /{" "}
                {formatThousand(usageData?.total_value ?? 0)}{" "}
                {usageData?.unit_value ?? ""}
              </Text>
            </ConditionalRendering>
            <ConditionalRendering
              condition={usageData?.product_service === "period"}
            >
              <div className="py-2">
                <Progress
                  value={usageData?.usage_percent ?? 0}
                  size="xl"
                  radius="md"
                />
              </div>
              <div className="flex justify-between">
                <Text className="text-fjord-grey-500">
                  {formatThousand(usageData?.used_value ?? 0)} /{" "}
                  {formatThousand(usageData?.total_value ?? 0)}{" "}
                  {capitalizeFirstLetter(usageData?.unit_value ?? "")}
                </Text>
                <ConditionalRendering condition={isValidEndDate}>
                  <Text>
                    Exp. {formatDateWithDynamicTimezone(usageData?.end_date!)}
                  </Text>
                </ConditionalRendering>
              </div>
            </ConditionalRendering>
          </div>
        </div>
      </ConditionalRendering>
    </Modal>
  );
};
